import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

export default function StaffOrderNotes({ orderId, token }) {
  const [details, setDetails] = useState(null);
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const base = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}`;
  const load = useCallback(async () => {
    try { setDetails((await axios.get(`${base}/staff-details`, { headers: { Authorization: `Bearer ${token}` } })).data); }
    catch { setError('Could not load order notes and payment details.'); }
  }, [base, token]);
  useEffect(() => { load(); }, [load]);
  const action = async (path, payload = {}) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const response = await axios.post(`${base}/${path}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      if (path === 'notes') { setBody(''); setMessage('Private note saved.'); }
      else setMessage(path === 'sync' ? 'Sync queued. Refresh shortly to see changes.' : response.data.queued ? 'Portal access email queued.' : 'An access link was requested recently. Ask the customer to check their inbox.');
      await load();
    } catch (err) { setError(err.response?.data?.detail || 'Could not save this change.'); }
    finally { setBusy(false); }
  };
  return <section className="bg-card rounded-lg border border-border p-6 space-y-4">
    <h2 className="font-semibold">Payment &amp; staff notes</h2>
    {details && <>
      <p className="text-sm">{details.order_source === 'in_store' ? `In-store ticket ${details.in_store_ticket || 'not recorded'}` : 'Online order'} · {details.payment_status}</p>
      <p className="text-sm">Paid: ${Number(details.amount_paid || 0).toFixed(2)} · Balance: ${Number(details.balance_due || 0).toFixed(2)}</p>
      {details.payment_review && <p role="alert" className="text-sm text-red-700">{details.payment_review}</p>}
      {details.crm_sync_error && <p role="alert" className="text-sm text-red-700">CRM sync: {details.crm_sync_error}</p>}
      <div className="flex flex-wrap gap-2">
        <button disabled={busy} onClick={() => action('sync')} className="min-h-[44px] border rounded px-3 text-sm">Sync with Prometheus</button>
        <button disabled={busy} onClick={() => action('portal-invitation')} className="min-h-[44px] border rounded px-3 text-sm">Send portal setup email</button>
        <button disabled={busy} onClick={load} className="min-h-[44px] px-3 text-sm underline">Refresh</button>
      </div>
    </>}
    <form onSubmit={(event) => { event.preventDefault(); action('notes', { body, category }); }} className="space-y-3">
      <label className="block text-sm">Note category
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="block w-full min-h-[44px] mt-1 rounded border bg-background p-2">
          {['general', 'sizing', 'garment', 'decoration', 'approval'].map(c => <option value={c} key={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
        </select>
      </label>
      <label className="block text-sm">Private staff note
        <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} required rows={4}
          className="mt-1 block w-full rounded border bg-background p-3" />
      </label>
      <p className="text-xs text-muted-foreground">Visible to staff only. Add a new note for corrections to preserve the history.</p>
      <button disabled={busy || !body.trim()} className="min-h-[44px] rounded bg-primary px-4 text-primary-foreground disabled:opacity-50">{busy ? 'Saving…' : 'Add private note'}</button>
    </form>
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {message && <p role="status" className="text-sm text-green-700">{message}</p>}
    {details?.notes.map(note => <article key={note.id} className="border-t pt-3 text-sm">
      <p className="font-medium">{note.author || 'Staff'} · {note.category}</p>
      <p className="text-xs text-muted-foreground">{new Date(note.created_at).toLocaleString()}</p>
      <p className="mt-2 whitespace-pre-wrap break-words">{note.body}</p>
    </article>)}
    {details?.history.length > 0 && <div className="border-t pt-3"><h3 className="text-sm font-semibold">Previous customer orders</h3>
      <ul className="mt-2 space-y-1">{details.history.map(o => <li key={o.id}><Link className="inline-flex min-h-[44px] items-center text-sm underline" to={`/admin/orders/${o.id}`}>#{o.number} · {new Date(o.created_at).toLocaleDateString()}</Link></li>)}</ul>
    </div>}
  </section>;
}
